import { useState, useEffect, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { postNewFavorite } from '@shared/services/services';
import { ID_SITE } from '@shared/conf/env';

const validationSchema = Yup.object().shape({
  priority: Yup.number().required('Prioridad requerida'),
  available_sites: Yup.string()
    .matches(/^\d+(,\d+)*$/, 'Debe seleccionar por lo menos un sitio')
    .required('Debe seleccionar por lo menos un sitio'),
});

const initialValues = {
  priority: 1,
  sold: 10,
  main_image: '',
  id_emphasise_type: null,
  available_sites: ID_SITE,
};

export const useFavoriteForm = ({
  hotel,
  user,
  favMessage,
  hotReferenceCities,
  sites,
  isAdmin,
  setIsLoading,
  setIsShowAlert,
  setAlertOptions,
}) => {
  const getInitialSites = () => {
    if (isAdmin) return sites || [];
    const thisSite = sites?.find(x => Number(x.id) === Number(ID_SITE));
    return thisSite ? [thisSite] : [];
  };

  const [sitesToShow, setSitesToShow] = useState(getInitialSites);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const buildRequestBody = values => ({
    is_enabled: true,
    is_searchable: true,
    available_sites: values.available_sites?.split(','),
    hotReferenceCities,
    id_user: user?.id,
    hotels: [
      {
        name: hotel.name,
        latitude: String(hotel.location.latitude),
        longitude: String(hotel.location.longitude),
        priority: values.priority,
        sold: values.sold,
        main_image: values.main_image,
        id_emphasise_type: favMessage === 0 ? null : favMessage,
        id_integration: hotel.id_integration,
        id_provider: hotel.id_provider ?? null,
        id_city: String(hotel.id_city),
        code: String(hotel.code),
        id_hotel: String(hotel.id_hotel),
      },
    ],
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      setIsLoading(true);
      setErrorMessage('');
      try {
        const body = buildRequestBody(values);
        const result = await postNewFavorite(body);
        if (result[0]?.created_at) {
          hotel.nextSearch = true;
          hotel.nextSearchFav = true;
          setAlertOptions({
            type: 'success',
            msg: 'Hotel agregado a favoritos correctamente!',
          });
          setIsShowAlert(true);
          resetForm();
        } else {
          setAlertOptions({
            type: 'error',
            msg: 'Hotel NO agregado a favoritos!',
          });
          setIsShowAlert(true);
        }
      } catch (err) {
        console.error(
          '❌ Error en postNewFavorite:',
          err.response
            ? `HTTP ${err.response.status} - ${JSON.stringify(
                err.response.data
              )}`
            : err.request
            ? `No response - request enviado: ${err.request}`
            : `Error setup: ${err.message}`
        );
        setErrorMessage('Ocurrió un error inesperado. Intenta nuevamente.');
      } finally {
        setIsSubmitting(false);
        setIsLoading(false);
      }
    },
  });

  const handleSitesChange = useCallback(
    selected => {
      setSitesToShow(selected);
      const value = selected
        ?.map(x => +x.id)
        ?.filter(id => id !== 0)
        ?.join(',');
      formik.setFieldValue('available_sites', value);
    },
    [formik]
  );

  useEffect(() => {
    if (isAdmin && sites?.length) {
      const allSiteIds = sites
        .map(x => +x.id)
        .filter(id => id !== 0)
        .join(',');
      formik.setFieldValue('available_sites', allSiteIds);
    }
  }, [isAdmin, sites]);

  useEffect(() => {
    if (!isAdmin && sites?.length) {
      const thisSite = sites.find(x => Number(x.id) === Number(ID_SITE));
      setSitesToShow(thisSite ? [thisSite] : []);
    }
  }, [sites, isAdmin]);

  return {
    formik,
    sitesToShow,
    handleSitesChange,
    errorMessage,
    isSubmitting,
  };
};
